
import React from 'react';
import { ArrowLeft, BookOpen, Code, Database, Layers, Users, MapPin, BarChart2, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const Documentation = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <Link to="/" className="flex items-center text-purple-700 hover:text-purple-900">
            <ArrowLeft size={16} className="mr-2" />
            Back to Dashboard
          </Link>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex items-center mb-6">
            <BookOpen size={24} className="text-purple-700 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Fuel.AI Platform Documentation</h1>
          </div>
          
          <div className="prose max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              Welcome to the comprehensive documentation for the Fuel.AI Analytics Platform. 
              This guide outlines the platform's features, functionalities, and how to leverage 
              its AI-powered capabilities for business intelligence in the fuel retail sector.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h2 className="text-xl font-semibold text-purple-900 mb-3 flex items-center">
                  <Layers size={20} className="mr-2 text-purple-700" />
                  Platform Architecture
                </h2>
                <p className="text-gray-700 mb-4">
                  The Fuel.AI platform is built on a modern analytics architecture that integrates 
                  data from multiple sources to provide actionable insights for fuel retail operations.
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1">
                  <li>React-based frontend with interactive visualizations</li>
                  <li>Real-time data processing capabilities</li>
                  <li>Secure authentication and role-based access</li>
                  <li>API integration with existing systems</li>
                </ul>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h2 className="text-xl font-semibold text-purple-900 mb-3 flex items-center">
                  <Zap size={20} className="mr-2 text-purple-700" />
                  AI Capabilities
                </h2>
                <p className="text-gray-700 mb-4">
                  The AI Agent provides contextual insights and recommendations based on your data 
                  and business context.
                </p>
                <ul className="list-disc pl-5 text-gray-700 space-y-1">
                  <li>Natural language queries for data exploration</li>
                  <li>Predictive analytics for business forecasting</li>
                  <li>Anomaly detection for critical alerts</li>
                  <li>Root cause analysis for problem-solving</li>
                  <li>Scenario modeling for decision support</li>
                </ul>
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 mb-4">Core Modules</h2>
            
            <div className="space-y-8">
              <ModuleSection 
                icon={BarChart2}
                title="Executive Dashboard"
                description="High-level KPIs and performance metrics for executive decision-making."
                features={[
                  "Consolidated view of company-wide performance",
                  "Volume, profit, and revenue trend analysis",
                  "Segment performance comparison",
                  "Critical alerts monitoring",
                  "Café Amazon performance tracking"
                ]}
                path="/executive-dashboard"
              />
              
              <ModuleSection 
                icon={Users}
                title="Segmentation Explorer"
                description="Detailed analysis of customer segments and their performance."
                features={[
                  "Overview of all customer segments",
                  "Detailed segment profiles and demographics",
                  "Performance metrics by segment",
                  "Daypart activity analysis",
                  "Map visualization of segment distribution"
                ]}
                path="/segmentation-explorer"
              />
              
              <ModuleSection 
                icon={MapPin}
                title="Trade Area Analyzer"
                description="Geographic and spatial analysis of station trade areas."
                features={[
                  "Station selection with filtering capabilities",
                  "Market overview with competitive positioning",
                  "Competitor analysis within trade areas",
                  "Customer flow patterns and traffic analysis",
                  "Price elasticity modeling"
                ]}
                path="/trade-area-analyzer"
              />
              
              <ModuleSection 
                icon={Target}
                title="Competitor 360"
                description="Comprehensive analysis of competitor positioning and performance."
                features={[
                  "Competitive landscape overview",
                  "Pricing strategy comparison",
                  "Station network analysis",
                  "Traffic analysis by competitor",
                  "Segment and station filtering"
                ]}
                path="/competitor-360"
              />
              
              <ModuleSection 
                icon={Target}
                title="Campaign Management"
                description="Planning, execution, and analysis of marketing campaigns."
                features={[
                  "Campaign overview and performance metrics",
                  "Active campaign monitoring",
                  "Calendar view for campaign scheduling",
                  "Performance analysis with ROI tracking",
                  "Campaign creation workflow"
                ]}
                path="/campaign-management"
              />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 my-8">Technical Specifications</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <TechSpecCard 
                icon={Code}
                title="Frontend Technology"
                specs={[
                  "React.js for UI components",
                  "Tailwind CSS for styling",
                  "Recharts for data visualization",
                  "React Router for navigation",
                  "Lucide icons for visual elements"
                ]}
              />
              
              <TechSpecCard 
                icon={Database}
                title="Backend Services"
                specs={[
                  "RESTful API architecture",
                  "Real-time data processing",
                  "Secure authentication services",
                  "Caching for performance optimization",
                  "Scheduled data refresh"
                ]}
              />
              
              <TechSpecCard 
                icon={Zap}
                title="AI Components"
                specs={[
                  "Natural language processing",
                  "Machine learning models",
                  "Predictive analytics engine",
                  "Anomaly detection algorithms",
                  "Recommendation systems"
                ]}
              />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 mb-4">Getting Started</h2>
            
            <div className="space-y-4 mb-8">
              <p className="text-gray-700">
                To begin using the Fuel.AI platform:
              </p>
              
              <ol className="list-decimal pl-5 text-gray-700 space-y-2">
                <li>
                  <strong>Navigate to the Home dashboard</strong> for an overview of all 
                  available modules and recent insights.
                </li>
                <li>
                  <strong>Select a specific module</strong> from the left navigation menu 
                  based on your analysis needs.
                </li>
                <li>
                  <strong>Use the AI Agent</strong> by clicking the bot icon in the bottom 
                  left corner for contextual insights and recommendations.
                </li>
                <li>
                  <strong>Apply filters</strong> within each module to focus on specific 
                  segments, time periods, or categories.
                </li>
                <li>
                  <strong>Export data</strong> using the export buttons available in most views 
                  for further analysis in other tools.
                </li>
              </ol>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Best Practices</h3>
              <ul className="list-disc pl-5 text-gray-700 space-y-1">
                <li>Regularly review the Executive Dashboard for high-level insights</li>
                <li>Utilize segment filters to compare performance across different customer groups</li>
                <li>Combine trade area analysis with competitor data for location-based strategies</li>
                <li>Use the AI Agent to identify root causes of performance changes</li>
                <li>Export specific visualizations for inclusion in reports and presentations</li>
              </ul>
            </div>
            
            <p className="text-gray-700">
              For additional support or feature requests, please contact the Fuel.AI support team 
              through the help menu or by emailing support@fuel-ai.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for module sections
const ModuleSection = ({ icon: Icon, title, description, features, path }) => {
  return (
    <div className="border-l-4 border-purple-500 pl-6">
      <div className="flex items-start">
        <div className="bg-purple-100 p-2 rounded-lg mr-4">
          <Icon size={24} className="text-purple-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-700 mb-3">{description}</p>
          
          <ul className="list-disc pl-5 text-gray-700 space-y-1 mb-3">
            {features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
          
          <Link to={path} className="text-sm text-purple-700 hover:text-purple-900 font-medium">
            Go to {title} →
          </Link>
        </div>
      </div>
    </div>
  );
};

// Helper component for technical specification cards
const TechSpecCard = ({ icon: Icon, title, specs }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <Icon size={20} className="text-purple-700 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <ul className="text-sm text-gray-700 space-y-2">
        {specs.map((spec, index) => (
          <li key={index} className="flex items-start">
            <span className="text-purple-600 mr-2">•</span>
            {spec}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Documentation;
